import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { DEAL_PARTICIPANTS_SECTION_ID, type PmpDealType, pmpDealType } from '@openx/types/deal.ts';
import type { WithRequired } from '@openx/types/helpers.ts';
import type { OptionStateMap } from '@openx/types/options.ts';
import { type JSX, useCallback, useEffect } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import type { IParticipantFunctions } from '../../utils/participantFunctions.ts';
import { SupplementalAdQuality } from '../SupplementalAdQuality/SupplementalAdQuality.tsx';
import { Participant } from './components/Participant.tsx';
import { DealParticipantsField } from './fields.ts';

const MULTI_PARTNER_DEAL_TYPES: Set<PmpDealType> = new Set([pmpDealType.PrivateAuction, pmpDealType.WithinOpenAuction]);

export type DealParticipantsProps = {
  isEdit: boolean;
  dealType: PmpDealType | null;
  participantFunctions: IParticipantFunctions;
  useDemandPartnersFetch: (filters?: {
    account_uid?: string | null;
    enabled?: boolean;
  }) => WithRequired<OptionStateMap, 'refetch'>;
  blockedToSelectPartnerIds?: string[];
  isSection?: boolean;
};

export const DealParticipants = ({
  isEdit,
  dealType,
  participantFunctions,
  useDemandPartnersFetch,
  blockedToSelectPartnerIds,
  isSection = true,
}: DealParticipantsProps): JSX.Element => {
  const { t } = useTranslation();

  const { useOnBehalfOfAccountId, isOa } = useDealContext();

  const onBehalfOfAccountId = useOnBehalfOfAccountId();

  const { fields, append, remove } = useFieldArray({
    name: DealParticipantsField.DEAL_PARTICIPANTS,
  });

  const {
    mapById: demandPartners,
    loading: demandPartnersLoading,
    refetch: refetchDemandPartners,
  } = useDemandPartnersFetch({ account_uid: onBehalfOfAccountId });

  const observableParticipants = useWatch({ name: DealParticipantsField.DEAL_PARTICIPANTS });

  useEffect(() => {
    refetchDemandPartners();
  }, [refetchDemandPartners]);

  const removeParticipant = useCallback((index: number) => remove(index), [remove]);

  const addParticipant = useCallback(() => {
    const newParticipant = {
      [DealParticipantsField.DSP]: null,
      [DealParticipantsField.BUYER_IDS]: [],
      [DealParticipantsField.MAIN_BUYER]: null,
      [DealParticipantsField.BRAND_IDS]: [],
    };

    append(newParticipant);
  }, [append]);

  const selectedDemandPartners = (observableParticipants || []).map(({ demand_partner }) => demand_partner as string);

  return (
    <Box id={DEAL_PARTICIPANTS_SECTION_ID}>
      {isSection && (
        <Typography variant="h3" gutterBottom>
          {t('Participants')}
        </Typography>
      )}

      {!isOa && <SupplementalAdQuality />}

      {fields.map((field, index) => (
        <Participant
          dealType={dealType}
          demandPartnerOptions={demandPartners}
          demandPartnerOptionsLoading={demandPartnersLoading}
          blockedToSelectPartnerIds={blockedToSelectPartnerIds}
          index={index}
          isEdit={isEdit}
          key={field.id}
          participantFunctions={participantFunctions}
          remove={removeParticipant}
          selectedDemandPartners={selectedDemandPartners}
          showDivider={fields.length - index - 1 > 0}
          showRemove={fields.length > 1}
        />
      ))}

      {dealType && MULTI_PARTNER_DEAL_TYPES.has(dealType) && (
        <Box>
          <Button
            variant="text"
            color="primary"
            onClick={addParticipant}
            type="button"
            data-test="deal-participants-add-dsp"
          >
            {t('Add another DSP')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
